import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { ColorPicker } from '../color-picker/colorPicker';
import Color from 'color';

import styles from './styles.less';

interface ColorEncodingPanelProps {
    anchorEl?: HTMLElement | null;
    type: string;
    collectionId: string | number;
    onEncodingChange: (params: {
        index: string | number;
        value: string | null;
        encodingType: string;
        graphType: string;
    }) => void;
    encodingType: string;
    color?: string;
}

interface ColorResult {
    hex: string;
}

export const ColorEncodingPanel: React.FC<ColorEncodingPanelProps> = ({
    anchorEl,
    type,
    collectionId,
    onEncodingChange,
    encodingType,
    color
}) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const colorPalettes: string[][] = [
        ['#E3F2FD', '#90CAF9', '#42A5F5', '#1E88E5', '#1565C0', '#0D47A1', '#082A5E', '#05183A', '#020B1C'],
        ['#E8F5E9', '#A5D6A7', '#66BB6A', '#43A047', '#2E7D32', '#1B5E20', '#11421B', '#0A2810', '#05140A'],
        ['#F3E5F5', '#CE93D8', '#AB47BC', '#8E24AA', '#6A1B9A', '#4A148C', '#320F5E', '#1F0938', '#0F041A'],
        ['#FFEBEE', '#EF9A9A', '#EF5350', '#E53935', '#C62828', '#B71C1C', '#7A1313', '#4D0C0C', '#260606'],
        ['#FFFDE7', '#FFF59D', '#FFEE58', '#FDD835', '#F9A825', '#F57F17', '#B85E0B', '#7A3F08', '#3D1F04']
    ];

    const isCustom = !colorPalettes.flat().includes(color || '');
    const isPassthrough = encodingType === 'passthrough';

    const [customColor, setCustomColor] = useState<Color>(
        isCustom && color !== undefined
            ? Color(color)
            : Color('#FFFFFF')
    );
    const [tempColor, setTempColor] = useState<Color>(customColor);
    const isCustomColorSelected = isCustom && !isPassthrough && color !== undefined;

    const handleColorSelect = (id: string | number, paletteColor: string): void => {
        onEncodingChange({
            index: id,
            value: paletteColor,
            encodingType: 'color',
            graphType: type.toLowerCase()
        });
    };

    const handleColorChange = (color: ColorResult): void => {
        setTempColor(Color(color.hex));
    };

    const handleColorChangeComplete = (color: ColorResult): void => {
        const newColor = Color(color.hex);
        setCustomColor(newColor);
        setTempColor(newColor);
        handleColorSelect(collectionId, color.hex);
    };

    const pickColorButtonRef = useRef<HTMLLabelElement>(null);

    const handlePassThroughToggle = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const isPassthrough = e.target.checked;
        onEncodingChange({
            index: collectionId,
            value: isPassthrough ? null : color || null,
            encodingType: 'color',
            graphType: type.toLowerCase()
        });
    };

    return (
        <div
            ref={panelRef}
            className={styles['color-encoding-panel']}
            onMouseDown={(e) => {
                e.stopPropagation();
            }}
        >
            <h2>Color Encoding</h2>
            <div className={styles['encoding-options']}>
                <label className={styles['toggle-option']}>
                    <span>Pass Through Encoding</span>
                    <input
                        type="checkbox"
                        checked={isPassthrough}
                        onChange={handlePassThroughToggle}
                        disabled={collectionId === 'global' || collectionId === -1}
                    />
                </label>
            </div>

            <div className={styles['color-palettes']}>
                {colorPalettes.map((palette, i) => (
                    <div
                        key={i}
                        className={styles['palette-row']}
                    >
                        {palette.map((paletteColor, j) => (
                            <div
                                key={j}
                                className={classNames(styles['color-box'], color === paletteColor && styles['selected'])}
                                style={{ backgroundColor: paletteColor }}
                                onClick={() => handleColorSelect(collectionId, paletteColor)}
                            />
                        ))}
                    </div>
                ))}
            </div>

            <div className={styles['encoding-footer']}>
                <label
                    className={classNames(
                        styles['pick-color-button'],
                        isCustomColorSelected && styles['selected']
                    )}
                    ref={pickColorButtonRef}
                >
                    <span><b>Pick Custom Color</b></span>
                    <div
                        className={styles['picker-wrapper']}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                    >
                        <ColorPicker
                            style={{display: 'flex'}}
                            color={customColor}
                            onChange={handleColorChange}
                            onChangeComplete={handleColorChangeComplete}
                            disableAlpha={true}
                        />
                    </div>
                </label>
            </div>
        </div>
    );
};
